import { useLocation, useNavigate } from "react-router-dom";
import styles from "./ForgottenUsername.module.scss";
import { isValidEmail, isValidPostcode } from "application/utilities/validations";
import DropDown from "presentation/components/DropDown/DropDown";
import InputField from "presentation/components/InputField/InputField";
import Button from "presentation/components/Button/Button";
import { useEffect, useState, useRef } from "react";
import api from "application/utilities/axiosIntance";
import { Link } from 'react-router-dom';
import { Site } from "application/types/Site";
import { Spinner } from "react-bootstrap";
import { Result } from "application/types/GetLoginDetails";
import { renderLinkOrSpan } from "presentation/components/SupportText/SupportText";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { useTranslation } from 'react-i18next';
import { SiteType } from "application/types/SiteType";

const ForgottenUsername = () => {

    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [postCode, setPostCode] = useState("");
    const [surname, setSurname] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [message, setMessage] = useState("");
    const [loader, setLoader] = useState(false);
    const [siteListLoaded, setSiteListLoaded] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState("");
    const [siteList, setSiteList] = useState<Site[]>([]);
    const [isTB, setIsTB] = useState(false);
    const emailRef = useRef<HTMLInputElement | null>(null);
    const region = useSelector((state: RootState) => state.region.region);
    const location = useLocation();
    const fromTBFlow = location.state?.fromTBFlow || false;
    const forwardedProduct = location.state?.product || "";
    const { t } = useTranslation();

    const userEmail = location.state?.email || "";
    const layoutState = useSelector((state: RootState) => state.layout) as {
        data: Result;
        isLoading: boolean;
        error: string | null;
    };
    const { data, isLoading } = layoutState;

    const handleSubmit = () => {
        setErrorMessage("");
        setLoader(true);

        const errors = [];

        if (!selectedProduct) errors.push(t("FIELD_NAME_PRODUCT"));
        if (!email.trim()) errors.push(t("FIELD_NAME_EMAIL_ADDRESS"));

        if (!isTB && !password.trim()) {
            errors.push(t("FIELD_NAME_PASSWORD"));
        }

        if (isTB) {
            if (!postCode.trim()) errors.push("Postcode");
            if (!surname.trim()) errors.push("Surname");
            if (postCode && !isValidPostcode(postCode)) {
                setErrorMessage("Please enter a valid postcode");
                setLoader(false);
                return;
            }
        }

        if (errors.length > 0) {
            const last = errors.pop();
            const fields = errors.join(", ");
            const message = errors.length
                ? t("FIELD_REQUIRED_MULTIPLE", { fields, last })
                : t("FIELD_REQUIRED_SINGLE", { field: last })

            setErrorMessage(message.charAt(0).toUpperCase() + message.slice(1));
            setLoader(false);
            return;
        }

        if (!isValidEmail(email)) {
            setErrorMessage(t("INVALID_EMAIL"));
            setLoader(false);
            return;
        }

        const headers = !isTB
            ? { Authorization: `Basic ${btoa(password)}` }
            : {};

        api.post("/Login/ForgottenUserName",
            {
                regionCode: region,
                siteType: selectedProduct,
                email: email,
                surname: surname,
                sitePostCode: postCode,
            },
            { headers }
        )
            .then((response) => {
                const { isSuccess, result } = response.data;
                if (!isSuccess) {
                    setErrorMessage(result?.responseMessage);
                    return;
                }
                setMessage(result?.responseMessage);
            })
            .catch(() => {
                setErrorMessage(t("GENERIC_UNEXPECTED_ERROR"));
            })
            .finally(() => setLoader(false));
    }

    useEffect(() => {
        api.get("/Login/GetPortalSiteList?regionCode=" + region)
            .then((response) => {
                const data = response.data;
                if (data.isSuccess && data.result) {
                    setSiteList(data.result);
                    if (data.result.length === 1) {
                        setSelectedProduct(data.result[0].siteType);
                    }
                }
            })
            .catch((error) => {
                console.error(t("GENERIC_SOMETHING_WENT_WRONG"), error);
            })
            .finally(() => {
                setSiteListLoaded(true);
            });
    }, [region, t]);

    useEffect(() => {
        if (selectedProduct && siteListLoaded && emailRef.current) {
            emailRef.current.focus();
        }
    }, [siteListLoaded, selectedProduct]);

    useEffect(() => {
        if (fromTBFlow && forwardedProduct) {
            setSelectedProduct(forwardedProduct);
            setIsTB(forwardedProduct === SiteType.TRADE_BUYER);
        }
    }, [fromTBFlow, forwardedProduct]);

    useEffect(() => {
        if (fromTBFlow && userEmail) {
            setEmail(userEmail);
        }
    }, [fromTBFlow, userEmail]);


    if (isLoading || !siteListLoaded) {
        return <Spinner animation="border" variant="light" />;
    }

    const middleLinks = data?.middleLinks || [];
    const selectedSiteName =
        siteList.find(site => site.siteType === selectedProduct)?.siteName || selectedProduct;

    return (
        <div className="d-flex justify-content-center align-items-center w-100">
            <nav className={`${styles.popup} px-5 py-4 mb-2`}>
                {!message ?
                    (<>
                        <div className="align-items-center justify-content-center">
                            <div>
                                <div className={`${styles.heading}`}>
                                    {t("FORGOT_USERNAME_HEADING")}
                                </div>
                                <div className={`${styles.description}`}>
                                    {t("FORGOT_USERNAME_INSTRUCTION")}
                                </div>
                            </div>
                        </div>
                        {errorMessage &&
                            <div className={`${styles.error} d-flex align-items-center justify-content-center pt-2 ps-1`}>
                                {errorMessage}
                            </div>
                        }

                        <form onSubmit={(e) => e.preventDefault()}>
                            {fromTBFlow ? (
                                <InputField
                                    value={selectedSiteName}
                                    readOnly
                                    placeholder="Product"
                                />
                            ) : siteList.length === 1 ? (
                                <InputField
                                    value={siteList[0].siteName}
                                    readOnly
                                    placeholder="Product"
                                />
                            ) : (
                                <DropDown
                                    options={siteList}
                                    defaultOption="Select a Product"
                                    value={selectedProduct}
                                    onChange={(e) => {
                                        setSelectedProduct(e.target.value);
                                        setIsTB(e.target.value === SiteType.TRADE_BUYER);
                                        setEmail("");
                                        setPassword("");
                                        setPostCode("");
                                        setSurname("");
                                    }}
                                />
                            )}

                            <InputField maxLength={100} value={email} placeholder={t("FIELD_NAME_REGISTEREDEMAIL")}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} ref={emailRef} />
                            {
                                !isTB ?
                                    <InputField value={password} placeholder={t('FIELD_NAME_REGISTEREDPASSWORD', { defaultValue: t('FIELD_NAME_PASSWORD') })} type="password"
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)} />
                                    :
                                    <>
                                        <InputField maxLength={100} value={postCode} placeholder="Site Postcode"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPostCode(e.target.value.toUpperCase())} />
                                        <InputField maxLength={100} value={surname} placeholder="Surname"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSurname(e.target.value)} />
                                    </>
                            }
                            <Button type="submit" label={t("BUTTON_SUBMIT")} onClick={handleSubmit} loading={loader}></Button>
                            <Button label={t("BUTTON_CANCEL")} onClick={() => { navigate("/") }}></Button>
                        </form>

                        <div className="mt-3 mb-2">
                            <div className={`justify-content-center mt-2 text-center ${styles.helpText}`}>
                                {middleLinks[4] && renderLinkOrSpan(middleLinks[4], "")}
                                {middleLinks[5] && renderLinkOrSpan(middleLinks[5], styles.details, "", true)}
                            </div>
                        </div>
                    </>)
                    :
                    (<div className="align-items-center justify-content-center text-center">
                        <div className={`${styles.info} d-flex align-items-center justify-content-center pt-2 ps-1 mb-3`}>
                            {message}
                        </div>
                        <Link to="/" className="linkText ps-1">{t("RETURN_MESSAGE")}</Link>
                    </div>)}
            </nav>
        </div>
    )
}

export default ForgottenUsername;